import {
  appendFileSync,
  existsSync,
  lstatSync,
  mkdirSync,
  readdirSync,
  readFileSync,
  readlinkSync,
  rmSync,
  statSync,
  symlinkSync,
} from "node:fs";
import { join } from "node:path";
import type { BootstrapBundle } from "../setup/bootstrap-bundle-types.js";
import { redactToken, type GitFn } from "./git-prep.js";
import { computeGraphifyEnv } from "./graphify.js";
import type { BootLogger } from "./types.js";

/**
 * Ports the best-effort workspace extras that ride the git-ready gate:
 * - `bind_graphify_bundle` (entrypoint.sh:521-562)
 * - `ensure_grimoire_submodule` + `link_grimoire_skills` (entrypoint.sh:576-621)
 * - the reference-repos clone block (entrypoint.sh:793-827)
 *
 * Ordering invariant (mark_git_ready, entrypoint.sh:623-628): graphify bind +
 * grimoire submodule + skill links run BEFORE the ready flip (Claude must not
 * spawn before skills exist — fleet-wide 2026-07-10 incident); reference
 * repos run AFTER ready (supplementary context must never block Claude).
 * GitPrepJob's onReady/afterReady hooks carry that split.
 *
 * Every helper is best-effort: always returns cleanly, never fails the gate.
 */

function errText(err: unknown): string {
  return redactToken(err instanceof Error ? err.message : String(err));
}

function isDir(path: string): boolean {
  try {
    return statSync(path).isDirectory();
  } catch {
    return false;
  }
}

function isSymlink(path: string): boolean {
  try {
    return lstatSync(path).isSymbolicLink();
  } catch {
    return false;
  }
}

const GRAPHIFY_BUNDLE_FILES = [
  "graph.json",
  "GRAPH_REPORT.md",
  "manifest.json",
  ".graphify_analysis.json",
  ".graphify_labels.json",
  "publish-manifest.json",
  "cost.json",
];

/** Symlink the shared graphify bundle into <workspace>/graphify-out.
 *  `graphifyEnv` is computeGraphifyEnv's output — empty (disabled/no slug)
 *  makes this a no-op. */
export function bindGraphifyBundle(
  workspace: string,
  graphifyEnv: Record<string, string>,
  log: BootLogger,
): void {
  const slug = graphifyEnv.CONVEYOR_GRAPHIFY_SLUG;
  const sourceDir = graphifyEnv.CONVEYOR_GRAPHIFY_DIR;
  const graphFile = graphifyEnv.CONVEYOR_GRAPHIFY_GRAPH;
  if (!slug || !sourceDir || !graphFile) return;
  if (!existsSync(graphFile)) {
    log.info(`[boot] Graphify bundle not found for '${slug}' at ${sourceDir}`);
    return;
  }
  const targetDir = join(workspace, "graphify-out");
  try {
    mkdirSync(targetDir, { recursive: true });
  } catch {
    log.warn(`[boot] WARN: unable to create graphify-out at ${targetDir}`);
    return;
  }
  for (const rel of GRAPHIFY_BUNDLE_FILES) {
    const source = join(sourceDir, rel);
    const target = join(targetDir, rel);
    if (!existsSync(source)) continue;
    // -e || -L in bash: never clobber an existing entry, including a broken
    // symlink (existsSync follows links, hence the extra lstat check).
    if (existsSync(target) || isSymlink(target)) continue;
    try {
      symlinkSync(source, target);
    } catch {
      /* best-effort, bash `|| true` */
    }
  }
  log.info(`[boot] Bound graphify bundle '${slug}' into ${targetDir}`);
}

/** Materialize the .claude/grimoire submodule when the repo declares it and
 *  the bake didn't populate it. Always returns cleanly. */
export async function ensureGrimoireSubmodule(
  workspace: string,
  githubToken: string,
  git: GitFn,
  log: BootLogger,
): Promise<void> {
  try {
    if (!existsSync(join(workspace, ".gitmodules"))) return;
    let paths = "";
    try {
      ({ stdout: paths } = await git(
        ["config", "--file", ".gitmodules", "--get-regexp", String.raw`submodule\..*\.path`],
        { cwd: workspace, timeoutMs: 10_000 },
      ));
    } catch {
      // No submodule entries (config exits nonzero on no match).
      return;
    }
    if (!paths.split("\n").some((line) => line.trim().endsWith(".claude/grimoire"))) return;
    // Already materialized (pod-image bake ran conveyor-prebake successfully).
    if (isDir(join(workspace, ".claude", "grimoire", "skills"))) return;
    if (!githubToken) {
      log.warn("[boot] WARN: grimoire submodule absent and no token to fetch it");
      return;
    }
    // insteadOf injects the installation token for the submodule's https URL
    // the same way syncTaskBranchToRepo authenticates the main repo remote.
    try {
      await git(
        [
          "-c",
          `url.https://x-access-token:${githubToken}@github.com/.insteadOf=https://github.com/`,
          "submodule",
          "update",
          "--init",
          ".claude/grimoire",
        ],
        { cwd: workspace, timeoutMs: 300_000 },
      );
      log.info("[boot] Initialized grimoire submodule");
    } catch (err) {
      log.warn(
        `[boot] WARN: grimoire submodule init failed (baked pods may lack rc-* skills): ${errText(err)}`,
      );
    }
  } catch (err) {
    log.warn(`[boot] WARN: grimoire submodule check failed: ${errText(err)}`);
  }
}

/** Register pod-local skill symlinks in .git/info/exclude so `git add -A`
 *  (create_pull_request, WIP flush) can never sweep them into a commit.
 *  Repo .gitignore only covers `rc-*`; grimoire skills without that prefix
 *  (e.g. conveyor-plan) leaked into real PRs — 2026-07-27 fleet audit. */
function excludeLinkedSkills(workspace: string, names: string[], log: BootLogger): void {
  if (names.length === 0) return;
  const infoDir = join(workspace, ".git", "info");
  // A worktree checkout has a .git FILE — skip rather than misplace the exclude.
  if (!isDir(join(workspace, ".git"))) return;
  try {
    mkdirSync(infoDir, { recursive: true });
    const excludePath = join(infoDir, "exclude");
    const existing = existsSync(excludePath) ? readFileSync(excludePath, "utf8") : "";
    const missing = names
      .map((name) => `.claude/skills/${name}`)
      .filter((line) => !existing.split("\n").includes(line));
    if (missing.length === 0) return;
    const prefix = existing.length > 0 && !existing.endsWith("\n") ? "\n" : "";
    appendFileSync(excludePath, `${prefix}${missing.join("\n")}\n`);
  } catch (err) {
    log.warn(`[boot] WARN: unable to exclude grimoire skill links: ${errText(err)}`);
  }
}

function symlinkTargetsGrimoire(path: string): boolean {
  try {
    return readlinkSync(path).includes(join("grimoire", "skills"));
  } catch {
    return false;
  }
}

/** Link grimoire skills into the repo's project skill dir (pod-local; each
 *  link is registered in .git/info/exclude so it can never be committed).
 *  Never clobbers a real (repo-tracked) skill dir or a symlink owned by
 *  something else (e.g. @rallycry/conveyor-skills links into node_modules). */
export function linkGrimoireSkills(workspace: string, log: BootLogger): void {
  const sourceDir = join(workspace, ".claude", "grimoire", "skills");
  const targetDir = join(workspace, ".claude", "skills");
  if (!isDir(sourceDir)) return;
  try {
    mkdirSync(targetDir, { recursive: true });
  } catch {
    log.warn(`[boot] WARN: unable to create ${targetDir}; skipping grimoire skill links`);
    return;
  }
  const linkedNames: string[] = [];
  let names: string[] = [];
  try {
    names = readdirSync(sourceDir);
  } catch {
    return;
  }
  for (const name of names) {
    if (!isDir(join(sourceDir, name))) continue;
    const target = join(targetDir, name);
    // bash: [ -e ] && [ ! -L ] — a broken symlink is replaceable, a real dir is not.
    if (existsSync(target) && !isSymlink(target)) {
      log.warn(`[boot] WARN: ${target} exists and is not a symlink; skipping`);
      continue;
    }
    // A repo-owned symlink (e.g. a committed link into node_modules/@rallycry/
    // conveyor-skills) is TRACKED content — rewriting it to point at grimoire
    // would surface as a modification that `git add -A` sweeps into commits.
    // Only grimoire-owned links (broken or not) are ours to refresh.
    if (isSymlink(target) && !symlinkTargetsGrimoire(target)) continue;
    try {
      // ln -sfn: replace an existing symlink in place.
      rmSync(target, { force: true });
      symlinkSync(join("..", "grimoire", "skills", name), target);
      linkedNames.push(name);
    } catch {
      /* best-effort */
    }
  }
  excludeLinkedSkills(workspace, linkedNames, log);
  log.info(`[boot] Linked ${linkedNames.length} grimoire skills into ${targetDir}`);
}

/** Mark the grimoire submodule gitlink assume-unchanged so pointer drift after
 *  a pod resume never shows as `M .claude/grimoire` nor gets committed by the
 *  `git add -A` paths (create_pull_request, WIP flush). Agents hand-ran this in
 *  8 sessions of the 2026-07-27 fleet audit; in-pod submodule fetches fail
 *  (no credentials on the submodule remote), so the pointer is noise here. */
export async function neutralizeGrimoirePointer(
  workspace: string,
  git: GitFn,
  log: BootLogger,
): Promise<void> {
  if (!existsSync(join(workspace, ".claude", "grimoire"))) return;
  try {
    await git(["update-index", "--assume-unchanged", ".claude/grimoire"], {
      cwd: workspace,
      timeoutMs: 10_000,
    });
    log.info("[boot] Marked .claude/grimoire assume-unchanged (submodule pointer drift is noise)");
  } catch (err) {
    // Path not in the index (repo without the submodule) or index lock — noise either way.
    log.warn(`[boot] WARN: grimoire assume-unchanged failed: ${errText(err)}`);
  }
}

interface ReferenceRepo {
  slug: string;
  owner: string;
  name: string;
  branch: string;
  token: string;
}

function parseReferenceRepos(refsJson: string): ReferenceRepo[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(refsJson);
  } catch {
    // bash parity: jq parse errors were sent to /dev/null.
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const refs: ReferenceRepo[] = [];
  for (const entry of parsed) {
    if (typeof entry !== "object" || entry === null) continue;
    const raw = entry as Record<string, unknown>;
    const str = (key: string): string => (typeof raw[key] === "string" ? (raw[key] as string) : "");
    const ref: ReferenceRepo = {
      slug: str("slug"),
      owner: str("owner"),
      name: str("name"),
      branch: str("branch") || "main",
      token: str("token"),
    };
    if (ref.token && ref.slug && ref.owner && ref.name) refs.push(ref);
  }
  return refs;
}

/**
 * GitPrepJob.onReady production composition — the three pre-ready binds in
 * the pinned mark_git_ready order (graphify bind, grimoire submodule, skill
 * links). Best-effort like its parts; a throw here is downgraded to a warn by
 * GitPrepJob anyway.
 */
export async function runPreReadyBinds(
  workspace: string,
  bundle: BootstrapBundle,
  git: GitFn,
  log: BootLogger,
): Promise<void> {
  const graphifyEnv = computeGraphifyEnv(process.env, bundle.gitPlan.repoName, isDir);
  bindGraphifyBundle(workspace, graphifyEnv, log);
  await ensureGrimoireSubmodule(workspace, bundle.githubToken ?? "", git, log);
  linkGrimoireSkills(workspace, log);
  await neutralizeGrimoirePointer(workspace, git, log);
}

/**
 * Shallow-clone reference repos (from REFERENCE_REPOS_JSON in bundle.envVars)
 * as read-only context. Runs strictly AFTER the ready flip. Failures are
 * non-fatal — the task proceeds even when a reference project's GitHub App is
 * uninstalled or its token mint failed.
 */
export async function cloneReferenceRepos(
  refsJson: string | undefined,
  git: GitFn,
  log: BootLogger,
  referencesDir = "/workspaces/references",
): Promise<void> {
  if (!refsJson) return;
  const refs = parseReferenceRepos(refsJson);
  if (refs.length === 0) return;
  try {
    mkdirSync(referencesDir, { recursive: true });
  } catch {
    return;
  }
  for (const ref of refs) {
    const dest = join(referencesDir, ref.slug);
    if (existsSync(join(dest, ".git"))) continue;
    try {
      await git(
        [
          "clone",
          "--depth",
          "1",
          "--single-branch",
          "--branch",
          ref.branch,
          `https://x-access-token:${ref.token}@github.com/${ref.owner}/${ref.name}.git`,
          dest,
        ],
        { timeoutMs: 600_000 },
      );
      // Strip the token from the cloned remote so it never surfaces via
      // `git remote -v` when the agent inspects the reference repo.
      try {
        await git(
          ["remote", "set-url", "origin", `https://github.com/${ref.owner}/${ref.name}.git`],
          { cwd: dest, timeoutMs: 10_000 },
        );
      } catch {
        /* best-effort */
      }
      log.info(`[boot] cloned reference ${ref.slug} (${ref.owner}/${ref.name}@${ref.branch})`);
    } catch {
      log.warn(`[boot] WARN: reference clone failed: ${ref.slug}`);
    }
  }
}
